import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getDashboardOverview,
  getRevenueTimeline,
  getUserGrowthChart,
  getSubscriptionAnalytics,
  getSocialAccountsAnalytics,
  DashboardOverview,
  RevenueChartData,
  UserGrowthChartData,
  SubscriptionAnalytics,
  SocialAccountsAnalytics
} from 'services/admin/adminService';
import { AdminStatCard } from 'components/admin/AdminStatCard';
import { ChartCard } from 'components/admin/ChartCard';
import { TransactionsTable } from 'components/admin/TransactionsTable';
import { TopUsersCard } from 'components/admin/TopUsersCard';
import { LineChart } from 'components/dashboard/LineChart';
import { BarChart } from 'components/dashboard/BarChart';
import Spinner from 'components/common/Spinner';
import { UserTable } from 'components/dashboard/UserTable';
import { PaymentsTable } from 'components/admin/PaymentsTable';
import { useTheme } from 'contexts/ThemeContext';
import {
  LayoutDashboard,
  DollarSign,
  Users,
  Share2,
  TrendingUp,
  LayoutGrid,
  PieChart,
  Receipt,
  Trophy,
  HeartPulse,
  CheckCircle2,
  Clock,
  AlertTriangle,
  CheckCircle,
  ArrowDownCircle,
  BadgeDollarSign,
  CheckCheck,
  Zap,
  BarChart3,
  CreditCard,
  UserCheck,
  Star,
  UserPlus,
  Table2,
  Menu
} from 'lucide-react';
import { useSidebar } from 'contexts/SidebarContext';

type ActiveSection = 'overview' | 'analytics' | 'users' | 'payments';

export default function AdminDashboard() {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const { toggleSidebar } = useSidebar();

  // Theme tokens
  const containerBg = isDark ? '#1D293D' : '#F8FAFC';
  const mainBg = isDark ? '#0F172B' : '#FFFFFF';
  const mainShadow = isDark ? '0px 1px 2px rgba(2, 6, 24, 0.5)' : '0px 1px 2px rgba(43, 127, 255, 0.1)';
  const headerBorder = isDark ? '#1D293D' : '#F1F5F9';
  const titleColor = isDark ? '#E2E8F0' : '#314158';
  const subtitleColor = isDark ? '#90A1B9' : '#62748E';
  const cardBg = isDark ? '#1D293D' : '#FFFFFF';
  const cardBorder = isDark ? '#314158' : '#E2E8F0';
  const iconContainerBg = isDark ? '#1C398E' : '#EFF6FF';
  const iconColor = isDark ? '#51A2FF' : '#2B7FFF';
  const activeTabBg = isDark ? '#162456' : '#EFF6FF';
  const activeTabBorder = isDark ? '#155DFC' : '#8EC5FF';
  const activeTabText = isDark ? '#8EC5FF' : '#155DFC';
  const inactiveTabBg = isDark ? '#1D293D' : '#F1F5F9';
  const inactiveTabText = isDark ? '#90A1B9' : '#62748E';
  const successBg = isDark ? '#022C22' : '#ECFDF5';
  const successText = isDark ? '#5EE9B5' : '#009966';
  const infoBg = isDark ? '#162456' : '#EFF6FF';
  const infoText = isDark ? '#8EC5FF' : '#3B82F6';
  const selectBg = isDark ? '#1D293D' : '#FFFFFF';
  const selectBorder = isDark ? '#314158' : '#D1D5DB';
  const selectText = isDark ? '#E2E8F0' : '#374151';

  // State
  const [activeSection, setActiveSection] = useState<ActiveSection>('overview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Data state
  const [overview, setOverview] = useState<DashboardOverview | null>(null);
  const [revenueChart, setRevenueChart] = useState<RevenueChartData | null>(null);
  const [userGrowthChart, setUserGrowthChart] = useState<UserGrowthChartData | null>(null);
  const [subscriptionAnalytics, setSubscriptionAnalytics] = useState<SubscriptionAnalytics | null>(null);
  const [socialAccountsAnalytics, setSocialAccountsAnalytics] = useState<SocialAccountsAnalytics | null>(null);

  // Period filters
  const [revenuePeriod, setRevenuePeriod] = useState<'7d' | '30d' | '90d' | '12m'>('30d');
  const [userGrowthPeriod, setUserGrowthPeriod] = useState<'7d' | '30d' | '90d' | '12m'>('30d');

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [
          overviewData,
          revenueData,
          userGrowthData,
          subscriptionData,
          socialAccountsData
        ] = await Promise.all([
          getDashboardOverview(),
          getRevenueTimeline(revenuePeriod),
          getUserGrowthChart(userGrowthPeriod),
          getSubscriptionAnalytics(),
          getSocialAccountsAnalytics()
        ]);

        setOverview(overviewData);
        setRevenueChart(revenueData);
        setUserGrowthChart(userGrowthData);
        setSubscriptionAnalytics(subscriptionData);
        setSocialAccountsAnalytics(socialAccountsData);
        setError(null);
      } catch (err: any) {
        setError(err.message || t('admin.errors.failedToLoad', 'Failed to load dashboard data'));
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [revenuePeriod, userGrowthPeriod, t]);

  // Helper functions
  const formatCurrency = (value: number, currency: string = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatPercent = (value: number) => {
    return `${value.toFixed(1)}%`;
  };

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('en-US').format(value);
  };

  // Format chart data
  const formatRevenueChartData = (data: RevenueChartData | null) => {
    if (!data) return [];
    return data.data.map((item) => ({
      name: new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      revenue: item.revenue,
      transactions: item.transactions,
      netRevenue: item.netRevenue,
    }));
  };

  const formatUserGrowthChartData = (data: UserGrowthChartData | null) => {
    if (!data) return [];
    return data.data.map((item) => ({
      name: new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      total: item.total,
      new: item.new,
      churned: item.churned,
      netGrowth: item.netGrowth,
    }));
  };

  const formatSubscriptionDistributionData = () => {
    if (!subscriptionAnalytics) return [];
    return [
      { name: 'Trial', value: subscriptionAnalytics.overview.trial, color: '#94a3b8' },
      { name: 'Active', value: subscriptionAnalytics.overview.active, color: '#3b82f6' },
      { name: 'Cancelled', value: subscriptionAnalytics.overview.cancelled, color: '#ef4444' },
      { name: 'Suspended', value: subscriptionAnalytics.overview.suspended, color: '#f59e0b' },
    ];
  };

  // Error state
  if (error) {
    return (
      <div className="p-0 lg:pt-2 lg:pr-2 lg:pb-2 lg:pl-0" style={{ background: containerBg }}>
        <div
          className="rounded-none lg:rounded-2xl"
          style={{
            background: mainBg,
            boxShadow: mainShadow,
          }}
        >
          <div className="flex flex-col items-center justify-center py-20">
            <div style={{ color: isDark ? '#FF637E' : '#EF4444' }} className="text-2xl mb-2">
              <AlertTriangle size={28} />
            </div>
            <h3 className="text-base font-semibold mb-1" style={{ color: titleColor }}>
              {t('admin.errors.errorLoadingDashboard')}
            </h3>
            <p className="text-xs mb-2" style={{ color: subtitleColor }}>{error}</p>
            <button
              className="px-2 py-1 rounded-md transition-colors text-xs"
              style={{
                backgroundColor: isDark ? '#155DFC' : '#3B82F6',
                color: '#FFFFFF',
              }}
              onClick={() => window.location.reload()}
            >
              {t('admin.buttons.retry')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Loading state
  if (loading) {
    return <Spinner size="large" text={t('admin.loading.dashboard', 'Loading dashboard...')} fullPage />;
  }

  return (
    <div className="flex flex-col items-start flex-1 min-h-0 font-geist p-0 lg:pt-2 lg:pr-2 lg:pb-2 lg:pl-0" style={{ gap: '8px', background: containerBg }}>
      <div className="flex flex-col items-start flex-1 min-h-0 w-full overflow-hidden rounded-none lg:rounded-2xl"
        style={{ background: mainBg, boxShadow: mainShadow }}>

        {/* Header */}
        <div className="flex flex-col justify-center items-start w-full flex-shrink-0 rounded-none lg:rounded-t-2xl"
          style={{ padding: '12px 16px', gap: '8px', borderBottom: `1px solid ${headerBorder}` }}>
          <div className="flex flex-row items-center w-full" style={{ gap: '8px' }}>
            <button
              className="lg:hidden flex items-center justify-start flex-shrink-0"
              style={{ width: '24px', height: '36px', background: 'transparent', border: 'none', cursor: 'pointer', padding: 0 }}
              onClick={toggleSidebar}
            >
              <Menu size={24} color={isDark ? '#E2E8F0' : '#314158'} />
            </button>
            <div className="hidden lg:flex" style={{ width: '36px', height: '36px', background: iconContainerBg, borderRadius: '12px', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <LayoutDashboard size={20} style={{ color: iconColor }} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <h1 style={{ fontFamily: 'Geist, sans-serif', fontSize: '20px', lineHeight: '28px', fontWeight: 700, color: titleColor, margin: 0 }}>
                {t('admin.dashboard.title', 'Admin Dashboard')}
              </h1>
              <p className="hidden sm:block" style={{ fontFamily: 'Geist, sans-serif', fontSize: '12px', lineHeight: '16px', color: subtitleColor, margin: 0 }}>
                {t('admin.dashboard.description', 'Monitor and manage platform performance')}
              </p>
            </div>
          </div>
          {/* Tab Navigation */}
          <div className="w-full overflow-x-auto">
            <div style={{ display: 'flex', border: `1px solid ${headerBorder}`, borderRadius: '8px', overflow: 'hidden', height: '36px', width: 'fit-content' }}>
              {([
                { key: 'overview' as ActiveSection, label: t('admin.tabs.overview', 'Overview') },
                { key: 'analytics' as ActiveSection, label: t('admin.tabs.analytics', 'Analytics') },
                { key: 'users' as ActiveSection, label: t('admin.tabs.users', 'Users') },
                { key: 'payments' as ActiveSection, label: t('admin.tabs.payments', 'Payments') },
              ]).map((tab) => {
                const isActive = activeSection === tab.key;
                return (
                  <button
                    key={tab.key}
                    onClick={() => setActiveSection(tab.key)}
                    style={{
                      padding: '0 12px',
                      fontSize: '14px',
                      fontWeight: 500,
                      border: 'none',
                      cursor: 'pointer',
                      background: isActive ? (isDark ? '#1C398E' : '#EFF6FF') : 'transparent',
                      color: isActive ? (isDark ? '#51A2FF' : '#2B7FFF') : subtitleColor,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {tab.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className={`flex-1 min-h-0 overflow-y-auto ${isDark ? 'scrollbar-themed-dark' : 'scrollbar-themed-light'}`} style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '16px', width: '100%' }}>

          {/* Overview Section */}
          {activeSection === 'overview' && overview && (
            <div className="space-y-5">
              {/* Stats Cards Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                <AdminStatCard
                  title={t('admin.stats.revenue', 'Total Revenue')}
                  value={formatCurrency(overview.revenue.total, overview.revenue.currency)}
                  icon={<DollarSign size={20} color={iconColor} />}
                  trend={{
                    value: `${formatPercent(overview.revenue.growth)}`,
                    isPositive: overview.revenue.growth > 0
                  }}
                  subtitle={`MRR: ${formatCurrency(overview.revenue.mrr, overview.revenue.currency)}`}
                />
                <AdminStatCard
                  title={t('admin.stats.users', 'Total Users')}
                  value={formatNumber(overview.users.total)}
                  icon={<Users size={20} color={iconColor} />}
                  trend={{
                    value: `${formatPercent(overview.users.growth)}`,
                    isPositive: overview.users.growth > 0
                  }}
                  subtitle={`${overview.users.newThisMonth} ${t('admin.stats.newThisMonth')}`}
                />
                <AdminStatCard
                  title={t('admin.stats.socialAccounts', 'Social Accounts')}
                  value={formatNumber(overview.socialAccounts.total)}
                  icon={<Share2 size={20} color={iconColor} />}
                  trend={{
                    value: `${formatPercent(overview.socialAccounts.growth)}`,
                    isPositive: overview.socialAccounts.growth > 0
                  }}
                  subtitle={`${overview.socialAccounts.active} ${t('admin.stats.active')}`}
                />
                <AdminStatCard
                  title={t('admin.stats.conversionRate', 'Conversion Rate')}
                  value={formatPercent(overview.subscriptions.conversionRate)}
                  icon={<TrendingUp size={20} color={iconColor} />}
                  trend={{
                    value: `${overview.subscriptions.active} ${t('admin.stats.paid')}`,
                    isPositive: true
                  }}
                  subtitle={`${overview.subscriptions.trial} ${t('admin.stats.trialUsers')}`}
                />
              </div>

              {/* Revenue Chart */}
              <ChartCard
                title={t('admin.charts.revenueTimeline', 'Revenue Timeline')}
                icon={<TrendingUp size={16} color={iconColor} />}
                actions={
                  <select
                    value={revenuePeriod}
                    onChange={(e) => setRevenuePeriod(e.target.value as any)}
                    className="text-xs rounded-md px-2 py-1 focus:outline-none"
                    style={{
                      backgroundColor: selectBg,
                      border: `1px solid ${selectBorder}`,
                      color: selectText,
                    }}
                  >
                    <option value="7d">{t('admin.periods.last7Days')}</option>
                    <option value="30d">{t('admin.periods.last30Days')}</option>
                    <option value="90d">{t('admin.periods.last90Days')}</option>
                    <option value="12m">{t('admin.periods.last12Months')}</option>
                  </select>
                }
              >
                <LineChart
                  title=""
                  data={formatRevenueChartData(revenueChart)}
                  lines={[
                    { name: t('admin.charts.revenue', 'Revenue'), dataKey: 'revenue', color: '#10b981' },
                    { name: t('admin.charts.netRevenue', 'Net Revenue'), dataKey: 'netRevenue', color: '#3b82f6' }
                  ]}
                  yAxisLabel="Revenue ($)"
                />
                {revenueChart && (
                  <div
                    className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-4 pt-4"
                    style={{ borderTop: `1px solid ${headerBorder}` }}
                  >
                    <div className="text-center">
                      <div className="text-xs" style={{ color: subtitleColor }}>{t('admin.charts.total')}</div>
                      <div className="text-sm font-semibold" style={{ color: titleColor }}>
                        {formatCurrency(revenueChart.summary.totalRevenue)}
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-xs" style={{ color: subtitleColor }}>{t('admin.charts.avgDaily')}</div>
                      <div className="text-sm font-semibold" style={{ color: titleColor }}>
                        {formatCurrency(revenueChart.summary.averageDaily)}
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-xs" style={{ color: subtitleColor }}>{t('admin.charts.peak')}</div>
                      <div className="text-sm font-semibold" style={{ color: successText }}>
                        {formatCurrency(revenueChart.summary.peak)}
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-xs" style={{ color: subtitleColor }}>{t('admin.charts.lowest')}</div>
                      <div className="text-sm font-semibold" style={{ color: subtitleColor }}>
                        {formatCurrency(revenueChart.summary.lowest)}
                      </div>
                    </div>
                  </div>
                )}
              </ChartCard>

              {/* User Growth and Subscription Distribution */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                <ChartCard
                  title={t('admin.charts.userGrowth', 'User Growth')}
                  icon={<Users size={16} color={iconColor} />}
                  actions={
                    <select
                      value={userGrowthPeriod}
                      onChange={(e) => setUserGrowthPeriod(e.target.value as any)}
                      className="text-xs rounded-md px-2 py-1 focus:outline-none"
                      style={{
                        backgroundColor: selectBg,
                        border: `1px solid ${selectBorder}`,
                        color: selectText,
                      }}
                    >
                      <option value="7d">{t('admin.periods.last7Days')}</option>
                      <option value="30d">{t('admin.periods.last30Days')}</option>
                      <option value="90d">{t('admin.periods.last90Days')}</option>
                      <option value="12m">{t('admin.periods.last12Months')}</option>
                    </select>
                  }
                >
                  <LineChart
                    title=""
                    data={formatUserGrowthChartData(userGrowthChart)}
                    lines={[
                      { name: t('admin.charts.totalUsers'), dataKey: 'total', color: '#3b82f6' },
                      { name: t('admin.charts.newUsers'), dataKey: 'new', color: '#10b981' }
                    ]}
                    yAxisLabel="Users"
                  />
                </ChartCard>

                <ChartCard
                  title={t('admin.charts.subscriptionDistribution', 'Subscription Distribution')}
                  icon={<PieChart size={16} color={iconColor} />}
                >
                  <BarChart
                    title=""
                    data={formatSubscriptionDistributionData()}
                    bars={[{ name: t('admin.charts.subscriptions'), dataKey: 'value', color: '#8b5cf6' }]}
                    yAxisLabel="Count"
                  />
                </ChartCard>
              </div>

              {/* Recent Transactions */}
              <ChartCard
                title={t('admin.transactions.recent', 'Recent Transactions')}
                icon={<Receipt size={16} color={iconColor} />}
              >
                <TransactionsTable limit={10} />
              </ChartCard>

              {/* Top Performing Users and System Stats */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                <ChartCard
                  title={t('admin.topUsers.title', 'Top Performing Users')}
                  icon={<Trophy size={16} color={iconColor} />}
                >
                  <TopUsersCard metric="revenue" limit={5} />
                </ChartCard>

                <ChartCard
                  title={t('admin.system.health', 'System Health')}
                  icon={<HeartPulse size={16} color={iconColor} />}
                >
                  <div className="space-y-3">
                    <div
                      className="flex items-center justify-between p-3 rounded-lg"
                      style={{ backgroundColor: successBg }}
                    >
                      <div className="flex items-center gap-3">
                        <CheckCircle2 size={20} color={successText} />
                        <div>
                          <div className="text-sm font-medium" style={{ color: titleColor }}>
                            {t('admin.system.jobsSuccessRate')}
                          </div>
                          <div className="text-xs" style={{ color: subtitleColor }}>
                            {overview.system.jobsCompleted} {t('admin.system.completed')}, {overview.system.jobsFailed} {t('admin.system.failed')}
                          </div>
                        </div>
                      </div>
                      <div className="text-lg font-bold" style={{ color: successText }}>
                        {formatPercent(overview.system.successRate)}
                      </div>
                    </div>
                    <div
                      className="flex items-center justify-between p-3 rounded-lg"
                      style={{ backgroundColor: infoBg }}
                    >
                      <div className="flex items-center gap-3">
                        <Clock size={20} color={infoText} />
                        <div>
                          <div className="text-sm font-medium" style={{ color: titleColor }}>
                            {t('admin.system.scheduledJobs')}
                          </div>
                          <div className="text-xs" style={{ color: subtitleColor }}>
                            {t('admin.system.totalInQueue')}
                          </div>
                        </div>
                      </div>
                      <div className="text-lg font-bold" style={{ color: infoText }}>
                        {formatNumber(overview.system.jobsScheduled)}
                      </div>
                    </div>
                  </div>
                </ChartCard>
              </div>
            </div>
          )}

          {/* Analytics Section */}
          {activeSection === 'analytics' && subscriptionAnalytics && socialAccountsAnalytics && (
            <div className="space-y-5">
              {/* Subscription Analytics Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                <AdminStatCard
                  title={t('admin.subscriptions.active', 'Active Subscriptions')}
                  value={formatNumber(subscriptionAnalytics.overview.active)}
                  icon={<CheckCircle size={20} color={iconColor} />}
                />
                <AdminStatCard
                  title={t('admin.subscriptions.trial', 'Trial Users')}
                  value={formatNumber(subscriptionAnalytics.overview.trial)}
                  icon={<Clock size={20} color={iconColor} />}
                  subtitle={`${formatPercent(subscriptionAnalytics.conversion?.trialToStarter?.rate ?? 0)} ${t('admin.stats.conversion')}`}
                />
                <AdminStatCard
                  title={t('admin.subscriptions.churnRate', 'Churn Rate')}
                  value={formatPercent(subscriptionAnalytics.churn.rate)}
                  icon={<ArrowDownCircle size={20} color={iconColor} />}
                  subtitle={`${subscriptionAnalytics.churn.thisMonth} ${t('admin.stats.thisMonth')}`}
                />
                <AdminStatCard
                  title={t('admin.subscriptions.ltv', 'Average LTV')}
                  value={formatCurrency(subscriptionAnalytics.lifetime.averageLTV)}
                  icon={<BadgeDollarSign size={20} color={iconColor} />}
                  subtitle={`${subscriptionAnalytics.lifetime.averageSubscriptionLength.toFixed(1)} ${t('admin.stats.monthsAvg')}`}
                />
              </div>

              {/* Social Accounts Analytics */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                <AdminStatCard
                  title={t('admin.social.totalAccounts', 'Total Social Accounts')}
                  value={formatNumber(socialAccountsAnalytics.overview.total)}
                  icon={<Share2 size={20} color={iconColor} />}
                  trend={{
                    value: `${formatPercent(socialAccountsAnalytics.overview.growth)}`,
                    isPositive: socialAccountsAnalytics.overview.growth > 0
                  }}
                />
                <AdminStatCard
                  title={t('admin.social.activeAccounts', 'Active Accounts')}
                  value={formatNumber(socialAccountsAnalytics.overview.active)}
                  icon={<CheckCheck size={20} color={iconColor} />}
                />
                <AdminStatCard
                  title={t('admin.social.totalActions', 'Total Actions')}
                  value={formatNumber(socialAccountsAnalytics.activityStats.totalActionsPerformed)}
                  icon={<Zap size={20} color={iconColor} />}
                />
                <AdminStatCard
                  title={t('admin.social.successRate', 'Success Rate')}
                  value={formatPercent(socialAccountsAnalytics.healthMetrics.averageSuccessRate)}
                  icon={<TrendingUp size={20} color={iconColor} />}
                />
              </div>

              {/* Platform Breakdown */}
              <ChartCard
                title={t('admin.charts.platformBreakdown', 'Platform Breakdown')}
                icon={<BarChart3 size={16} color={iconColor} />}
              >
                <BarChart
                  title=""
                  data={socialAccountsAnalytics.byPlatform.map(p => ({
                    name: p.platform,
                    total: p.total,
                    active: p.active
                  }))}
                  bars={[
                    { name: t('admin.charts.total'), dataKey: 'total', color: '#3b82f6' },
                    { name: t('admin.stats.active'), dataKey: 'active', color: '#10b981' }
                  ]}
                  yAxisLabel="Accounts"
                />
              </ChartCard>

              {/* Churn Analysis */}
              <ChartCard
                title={t('admin.charts.churnAnalysis', 'Churn Analysis')}
                icon={<AlertTriangle size={16} color={iconColor} />}
              >
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <h4 className="text-sm font-semibold mb-3" style={{ color: subtitleColor }}>
                        {t('admin.charts.churnReasons')}
                      </h4>
                      {subscriptionAnalytics.churn.reasons.map((reason, index) => (
                        <div key={index} className="flex justify-between items-center mb-2">
                          <span className="text-sm" style={{ color: subtitleColor }}>{reason.reason}</span>
                          <span className="text-sm font-semibold" style={{ color: titleColor }}>{reason.count}</span>
                        </div>
                      ))}
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold mb-3" style={{ color: subtitleColor }}>
                        {t('admin.charts.recoveryAttempts')}
                      </h4>
                      <div className="space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="text-sm" style={{ color: subtitleColor }}>{t('admin.charts.sent')}</span>
                          <span className="text-sm font-semibold" style={{ color: infoText }}>
                            {subscriptionAnalytics.churn.recoveryAttempts.sent}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm" style={{ color: subtitleColor }}>{t('admin.charts.successful')}</span>
                          <span className="text-sm font-semibold" style={{ color: successText }}>
                            {subscriptionAnalytics.churn.recoveryAttempts.successful}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm" style={{ color: subtitleColor }}>{t('admin.charts.pending')}</span>
                          <span className="text-sm font-semibold" style={{ color: isDark ? '#FBBF24' : '#F59E0B' }}>
                            {subscriptionAnalytics.churn.recoveryAttempts.pending}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm" style={{ color: subtitleColor }}>{t('admin.charts.failed')}</span>
                          <span className="text-sm font-semibold" style={{ color: isDark ? '#FF637E' : '#EF4444' }}>
                            {subscriptionAnalytics.churn.recoveryAttempts.failed}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </ChartCard>
            </div>
          )}

          {/* Users Section */}
          {activeSection === 'users' && overview && (
            <div className="space-y-5">
              {/* User Stats */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-5">
                <AdminStatCard
                  title={t('admin.users.total', 'Total Users')}
                  value={formatNumber(overview.users.total)}
                  icon={<Users size={20} color={iconColor} />}
                />
                <AdminStatCard
                  title={t('admin.users.active', 'Active Users')}
                  value={formatNumber(overview.users.active)}
                  icon={<UserCheck size={20} color={iconColor} />}
                />
                <AdminStatCard
                  title={t('admin.users.trial', 'Trial Users')}
                  value={formatNumber(overview.users.trial)}
                  icon={<Clock size={20} color={iconColor} />}
                />
                <AdminStatCard
                  title={t('admin.users.paid', 'Paid Users')}
                  value={formatNumber(overview.users.paid)}
                  icon={<Star size={20} color={iconColor} />}
                />
                <AdminStatCard
                  title={t('admin.users.newThisMonth', 'New This Month')}
                  value={formatNumber(overview.users.newThisMonth)}
                  icon={<UserPlus size={20} color={iconColor} />}
                />
              </div>

              {/* Users Table */}
              <ChartCard
                title={t('admin.users.list', 'Users List')}
                icon={<Table2 size={16} color={iconColor} />}
              >
                <UserTable />
              </ChartCard>
            </div>
          )}

          {/* Payments Section */}
          {activeSection === 'payments' && (
            <ChartCard
              title={t('admin.payments.title', 'Payments & Transactions')}
              icon={<CreditCard size={16} color={iconColor} />}
            >
              <PaymentsTable />
            </ChartCard>
          )}
        </div>
      </div>
    </div>
  );
}
